// LICENSE_CODE ZON
'use strict';/*jslint ts:true, react: true*/
import React, {useCallback, useContext, useEffect, useRef,
    useState} from 'react';
import cn from 'classnames';
import etask from 'zetask';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import Icon_triangle_down from '../../img/icons/16/triangle_down.svg';
import Icon_close from '../../img/icons/16/close.svg';
import Icon_paste from '../../img/icons/16/paste.svg';
import Icon_check_line from '../../img/icons/24/check_line.svg';
import {use_ip_list, get_myip, IP_ERROR_CODES} from './use_ip';
import {Button} from '../../lib/button';
import {Text_input} from '../../../../shared/ui/input';
import {use_app_context} from '../../../../toolip/pub/lib/app_context';
import {WHITELIST_SUCCESS_CODES, use_whitelist} from './use_whitelist';
import {Btn_loader} from '../common/btn_loader';
import {find_order_by_id} from '../../../../toolip/pub/ui/dashboard/utils';

import styles from './ip_whitelist.module.less';

const LIST_INIT_HEIGHT = 36;
const FORM_INIT_HEIGHT = 78;
const SUCCESS_MSG_TIMEOUT = 2000;

const error_msg = {
    [IP_ERROR_CODES.INVALID]: 'Invalid number format',
    [IP_ERROR_CODES.USED]: 'Address already added',
};

const use_expand = (ref, value, render_key?)=>{
    const [is_expanded, set_is_expanded] = useState(false);
    const [max_height, set_max_height] = useState<number>(value);
    const toggle = useCallback((v?: boolean)=>{
        if (v===undefined)
            set_is_expanded(prev=>!prev);
        else
            set_is_expanded(v);
    }, []);
    useEffect(()=>{
        if (ref.current)
        {
            set_max_height(is_expanded
                ? ref.current.scrollHeight + value + 100
                : value);
        }
    }, [is_expanded, ref, value, render_key]);
    return {is_expanded, max_height, toggle};
};

interface i_segment_input {
    ip: ReturnType<typeof use_ip_list>['ip'];
    index: number;
    cls?: string;
}

export const Segment_input = (props: i_segment_input)=>{
    const {ip, index, cls} = props;
    return <Text_input value={ip.segments[index]}
      on_change={v=>{ ip.set_segment(index, v); }}
      cls={cls ? cls : styles.segment_input} placeholder="000"
      input_props={{onPaste: ev=>{
        const data = ev.clipboardData.getData('text');
        ip.paste(data);
      }}}
    />;
};

interface i_ip_whitelist_props {
    zone_id: string;
}

export const IP_whitelist = (props: i_ip_whitelist_props)=>{
    const {zone_id} = props;
    const {theme} = useContext(Settings_context);
    const {user, dashboard} = use_app_context();
    const order = find_order_by_id(
        zone_id, dashboard?.info?.orders, dashboard?.info?.zone_groups);
    const wl = use_whitelist();
    const {ip, list, error, add_ip, remove_ip, paste_ip} = use_ip_list({
        initial_data: order?.ips_whitelist,
        on_add_ip: (added_ip, new_list)=>
            wl.add(added_ip, zone_id, new_list),
        on_remove_ip: (removed_ip, new_list)=>
            wl.remove(removed_ip, zone_id, new_list),
    });
    const form_ref = useRef<HTMLDivElement>();
    const list_ref = useRef<HTMLDivElement>();
    const ips = use_expand(list_ref, LIST_INIT_HEIGHT, list.length);
    const ips_height = ips.is_expanded ? ips.max_height : 0;
    const form = use_expand(form_ref,
        FORM_INIT_HEIGHT + ips_height, list.length);
    const is_empty = ip.segments.every(s=>!s);
    const is_added = wl.state.data==WHITELIST_SUCCESS_CODES.ADDED;
    const is_updating_wl = wl.state.is_adding || wl.state.is_removing
        || is_added;
    useEffect(()=>{
        const need_refresh = [
            WHITELIST_SUCCESS_CODES.ADDED,
            WHITELIST_SUCCESS_CODES.REMOVED
        ].includes(wl.state.data);
        if (need_refresh)
            dashboard.dispatch({type: 'refresh_orders'});
    }, [wl.state]);
    useEffect(()=>{
        if (is_added)
            setTimeout(()=>{ wl.reset(); }, SUCCESS_MSG_TIMEOUT);
    }, [is_added]);
    if (!user || !order)
        return null;
    const find_myip = ()=>etask(function*(){
        const myip = yield get_myip();
        paste_ip(myip);
    });
    const paste_from_clipboard = async()=>{
        try {
            const text = await navigator.clipboard.readText();
            ip.paste(text);
        } catch(e){ console.log(e); }
    };
    const add_btn_caption = wl.state.is_adding
        ? <Btn_loader/>
        : 'Add';
    const success_msg = <div className={cn(styles.success_msg,
      is_added && styles.visible)}>
      <Icon_check_line/> Added
    </div>;
    const root_cls = cn(styles.ip_whitelist, {
        [styles.expanded]: form.is_expanded,
        [styles.error]: !!error,
    });
    const root_style = {maxHeight: `${form.max_height}px`};
    const ips_cls = cn(styles.ips_list, {
        [styles.expanded]: ips.is_expanded,
    });
    const ips_style = {maxHeight: `${ips.max_height}px`};
    const separator_cls = cn(theme.p14, theme.med, styles.separator);
    return <div className={root_cls} style={root_style}>
      <div className={styles.root_title} onClick={()=>{
        ips.toggle(false);
        form.toggle();
      }}>
        <div className={styles.text_wrapper}>
        <p className={cn(theme.p18, theme.med, theme.color_n700)}>
          Authenticated IP
        </p>
        <p className={cn(theme.p12, styles.description)}>
          IP checking is an optional security feature</p>
        </div>
        <Icon_triangle_down/>
      </div>
      <div className={cn(styles.add_form, form.is_expanded && styles.visible)}
        ref={form_ref}>
        <div className={styles.inputs}>
          <div className={styles.segments}>
            <Segment_input ip={ip} index={0}/>
            <p className={separator_cls}>.</p>
            <Segment_input ip={ip} index={1}/>
            <p className={separator_cls}>.</p>
            <Segment_input ip={ip} index={2}/>
            <p className={separator_cls}>.</p>
            <Segment_input ip={ip} index={3}/>
            <div className={styles.action_icon}>
              {is_empty
                ? <Icon_paste onClick={()=>{ void paste_from_clipboard(); }}/>
                : <Icon_close onClick={()=>{ ip.reset(); }}/>}
            </div>
          </div>
          <Button caption={<>{add_btn_caption} {success_msg}</>}
            size="s" cls={styles.add_btn} on_click={add_ip}
            disabled={is_updating_wl}
          />
        </div>
        <p className={cn(theme.p12, styles.error_msg)}>
          {error_msg[error]}</p>
        <Button caption="Find my IP" color="secondary" size="xs"
          cls={styles.find_btn} on_click={find_myip}/>
        {list.length > 0 && <div className={ips_cls} style={ips_style}>
          <div className={styles.divider}/>
          <div className={styles.list_title}>
            <p className={cn(theme.p14, theme.med, theme.color_n700)}>
              List of added IP's
            </p>
            <a onClick={()=>ips.toggle()}>
              {ips.is_expanded ? 'Hide' : 'Show'}
            </a>
          </div>
          <div ref={list_ref} className={styles.list}>
            {list.map(v=>{
              return <div key={v} className={styles.added_ip}>
                {wl.state.is_removing && wl.state.data==v
                  ? <div className={styles.remove_loader}><Btn_loader/></div>
                  : <>
                    <p className={theme.p14}>{v}</p>
                    {!is_updating_wl &&
                      <Icon_close onClick={()=>remove_ip(v)}/>}
                  </>
                }
              </div>;
            })}
          </div>
        </div>}
      </div>
    </div>;
};
